import type { CardCategoryDocument, CardTemplateDocument, CardTemplateFieldDocument, CreatorInstructionDocument, StructuredCardDocument } from "./product";
import { normalizeLayoutDocument } from "./layouts";
import { buildRecommendedFileLayout } from "./layout-presets";

export interface StarterCategoryDefinition {
  name: string;
  description: string;
  enabled: boolean;
  visibleToPlayers: boolean;
  displayStyle: "grid" | "list" | "feature_grid";
  icon: string;
}

export interface StarterTemplateDefinition {
  name: string;
  description: string;
  fields: CardTemplateFieldDocument[];
  assistantInstruction: string;
  contentType: "json" | "markdown";
  designation: string;
  contextVisibility: "pinned" | "partial" | "searchable" | "hidden";
  imageStyleInstruction: string;
  showCoverImages: boolean;
  enabled: boolean;
}

export interface StarterPackItem {
  category: StarterCategoryDefinition;
  template: StarterTemplateDefinition;
}

export interface StarterPack {
  id: string;
  title: string;
  description: string;
  icon: string;
  genre: "fantasy" | "scifi" | "mystery" | "general";
  itemCount: number;
  items: StarterPackItem[];
}

const textField = (key: string, label: string, required = false, description = ""): CardTemplateFieldDocument => ({
  id: crypto.randomUUID(), key, label, description, type: "text", required, options: [], referenceCategoryIds: [],
});

const longTextField = (key: string, label: string, required = false, description = ""): CardTemplateFieldDocument => ({
  id: crypto.randomUUID(), key, label, description, type: "long_text", required, options: [], referenceCategoryIds: [],
});

const numberField = (key: string, label: string, required = false, description = ""): CardTemplateFieldDocument => ({
  id: crypto.randomUUID(), key, label, description, type: "number", required, options: [], referenceCategoryIds: [],
});

const selectField = (key: string, label: string, options: string[], required = false): CardTemplateFieldDocument => ({
  id: crypto.randomUUID(), key, label, description: "", type: "select", required,
  options: options.map((opt, i) => ({ id: `${key}_opt_${i}`, label: opt, value: opt })), referenceCategoryIds: [],
});

const levelField = (key: string, label: string, min = 1, max = 20): CardTemplateFieldDocument => ({
  id: crypto.randomUUID(), key, label, description: "", type: "level", required: false,
  options: Array.from({ length: max - min + 1 }, (_, i) => ({ id: `${key}_lvl_${i}`, label: `ระดับ ${i + min}`, value: i + min })),
  referenceCategoryIds: [],
});

function withRecommendedLayouts(template: CardTemplateDocument): CardTemplateDocument {
  return {
    ...template,
    layout: normalizeLayoutDocument(buildRecommendedFileLayout(template.designation, template.fields, "full")),
    layouts: {
      full: normalizeLayoutDocument(buildRecommendedFileLayout(template.designation, template.fields, "full")),
      compact: normalizeLayoutDocument(buildRecommendedFileLayout(template.designation, template.fields, "compact")),
      mention: normalizeLayoutDocument(buildRecommendedFileLayout(template.designation, template.fields, "mention")),
    },
  };
}

// 1. Fantasy & D&D 5e TTRPG Pack
const FANTASY_DND_PACK: StarterPack = {
  id: "fantasy_dnd",
  title: "Fantasy & D&D 5e Starter Pack",
  description: "ชุดเทมเพลตมาตรฐานสำหรับโลกแฟนตาซีและเกม TTRPG: ตัวละคร, NPC, คาถา, ไอเทมเวท, มอนสเตอร์, เควสต์ และสมาคม",
  icon: "🗡️",
  genre: "fantasy",
  itemCount: 8,
  items: [
    {
      category: { name: "ตัวละคร (Characters)", description: "ตัวละครผู้เล่นและสหายร่วมผจญภัย", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🧙‍♂️" },
      template: {
        name: "Character Sheet", description: "ข้อมูลตัวละคร ค่าสถานะ และภูมิหลัง",
        fields: [
          textField("name", "ชื่อตัวละคร", true),
          selectField("species", "เผ่าพันธุ์ (Species)", ["มนุษย์", "เอลฟ์", "ดวอร์ฟ", "ฮาล์ฟลิง", "ไทฟลิง", "ดรากอนบอร์น", "ออร์ค", "ครึ่งเอลฟ์", "อื่นๆ"]),
          selectField("class", "คลาส (Class)", ["Fighter", "Wizard", "Rogue", "Cleric", "Paladin", "Barbarian", "Ranger", "Bard", "Druid", "Warlock", "Sorcerer", "Monk"]),
          levelField("level", "ระดับ (Level)", 1, 20),
          numberField("hp_max", "HP สูงสุด"),
          numberField("armor_class", "Armor Class (AC)"),
          textField("background", "ภูมิหลัง (Background)"),
          longTextField("personality", "บุคลิกภาพและเป้าหมาย"),
        ],
        assistantInstruction: "ตัวละครนี้มีค่าสถานะและการตัดสินใจที่สอดคล้องกับคลาสและระดับ",
        contentType: "json", designation: "character", contextVisibility: "partial", imageStyleInstruction: "แนวแฟนตาซี ภาพครึ่งตัว สไตล์ D&D concept art", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "บุคคลและ NPC (NPCs)", description: "ผู้คน ตัวละครเสริม และบุคคลสำคัญในโลก", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "👤" },
      template: {
        name: "NPC Profile", description: "ข้อมูลบุคคล นิสัย ความลับ และจุดยืน",
        fields: [
          textField("name", "ชื่อ-ฉายา", true),
          textField("role", "บทบาทหรืออาชีพ"),
          selectField("disposition", "ท่าทีต่อผู้เล่น", ["เป็นมิตรมาก", "เป็นมิตร", "เป็นกลาง", "ระแวง", "เป็นศัตรู"]),
          longTextField("motivation", "แรงจูงใจและเป้าหมาย"),
          longTextField("secrets", "ความลับหรือจุดอ่อน"),
          textField("voice_tone", "น้ำเสียงหรือกิริยาท่าทาง"),
        ],
        assistantInstruction: "NPC ต้องแสดงบทบาทและท่าทีตามความสัมพันธ์และแรงจูงใจที่ระบุไว้",
        contentType: "json", designation: "npc", contextVisibility: "partial", imageStyleInstruction: "ภาพพอร์ตเทรตบุคคล สไตล์แฟนตาซีสมจริง", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "คาถาและเวทมนตร์ (Spells)", description: "คัมภีร์เวทมนตร์และพลังเหนือธรรมชาติ", enabled: true, visibleToPlayers: true, displayStyle: "list", icon: "✨" },
      template: {
        name: "Spell Codex", description: "รายละเอียดคาถา ระดับ และผลกระทบ",
        fields: [
          textField("name", "ชื่อคาถา", true),
          selectField("level", "ระดับคาถา", ["Cantrip (ระดับ 0)", "ระดับ 1", "ระดับ 2", "ระดับ 3", "ระดับ 4", "ระดับ 5", "ระดับ 6", "ระดับ 7", "ระดับ 8", "ระดับ 9"]),
          selectField("school", "สายเวทมนตร์ (School)", ["Evocation", "Abjuration", "Conjuration", "Divination", "Enchantment", "Illusion", "Necromancy", "Transmutation"]),
          textField("casting_time", "ระยะเวลาร่าย (Casting Time)"),
          textField("range", "ระยะหวังผล (Range)"),
          textField("components", "องค์ประกอบ (V/S/M)"),
          textField("duration", "ระยะเวลาคงอยู่ (Duration)"),
          longTextField("effect", "ผลลัพธ์และคำอธิบาย"),
        ],
        assistantInstruction: "อ้างอิงเงื่อนไขและผลกระทบของคาถาตามที่ระบุในเอกสาร",
        contentType: "json", designation: "skill", contextVisibility: "searchable", imageStyleInstruction: "ภาพเอฟเฟกต์เวทมนตร์ สัญลักษณ์รูนเรืองแสง", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "ไอเทมและอาวุธเวท (Magic Items)", description: "ศาสตราวุธ ชุดเกราะ และโบราณวัตถุ", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "⚔️" },
      template: {
        name: "Magic Item", description: "คุณสมบัติ พลัง และประวัติของไอเทม",
        fields: [
          textField("name", "ชื่อไอเทม", true),
          selectField("rarity", "ความหายาก (Rarity)", ["ทั่วไป (Common)", "ไม่ธรรมดา (Uncommon)", "หายาก (Rare)", "หายากมาก (Very Rare)", "ในตำนาน (Legendary)", "สิ่งประดิษฐ์ (Artifact)"]),
          selectField("item_type", "ประเภท", ["อาวุธ", "ชุดเกราะ", "แหวน/สร้อย", "ไม้เท้า/คทา", "ยา/น้ำยา", "คัมภีร์", "ของวิเศษเบ็ดเตล็ด"]),
          selectField("attunement", "ต้องผูกพันจิต (Attunement)", ["ไม่จำเป็น", "จำเป็น", "เฉพาะคลาส/เผ่า"]),
          longTextField("properties", "คุณสมบัติพิเศษและพลัง"),
          textField("value", "มูลค่าประเมิน (เหรียญทอง)"),
        ],
        assistantInstruction: "บรรยายคุณสมบัติของไอเทมเมื่อถูกใช้งานตามความสามารถที่บันทึกไว้",
        contentType: "json", designation: "item", contextVisibility: "searchable", imageStyleInstruction: "ภาพไอเทมวัตถุโบราณ พื้นหลังเรียบ สไตล์ RPG icon", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "มอนสเตอร์และสัตว์อสูร (Monsters)", description: "สิ่งมีชีวิตอันตราย สัตว์ร้าย และศัตรู", enabled: true, visibleToPlayers: false, displayStyle: "grid", icon: "🐉" },
      template: {
        name: "Monster Statblock", description: "ข้อมูลการต่อสู้ สัญชาตญาณ และจุดอ่อน",
        fields: [
          textField("name", "ชื่อมอนสเตอร์", true),
          textField("cr", "ระดับความท้าทาย (CR)"),
          selectField("size", "ขนาด", ["เล็กจิ๋ว (Tiny)", "เล็ก (Small)", "กลาง (Medium)", "ใหญ่ (Large)", "ยักษ์ (Huge)", "มหึมา (Gargantuan)"]),
          textField("creature_type", "ประเภท (เช่น มังกร, สัตว์ประหลาด, ผีดิบ)"),
          numberField("armor_class", "AC"),
          numberField("hp", "HP สูงสุด"),
          textField("speed", "ความเร็วการเคลื่อนที่"),
          longTextField("actions", "ท่าโจมตีและความสามารถพิเศษ (Actions)"),
        ],
        assistantInstruction: "GM ใช้อ้างอิงการต่อสู้และความดุร้ายของสิ่งมีชีวิต ไม่เปิดเผยข้อมูลลับแก่ผู้เล่นโดยตรง",
        contentType: "json", designation: "npc", contextVisibility: "searchable", imageStyleInstruction: "ภาพมอนสเตอร์อันตราย สไตล์ creature design ดุดัน", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "เควสต์และภารกิจ (Quests)", description: "การผจญภัย เป้าหมาย และรางวัล", enabled: true, visibleToPlayers: true, displayStyle: "list", icon: "📜" },
      template: {
        name: "Quest Entry", description: "บันทึกเงื่อนไขและความคืบหน้าของเควสต์",
        fields: [
          textField("title", "ชื่อเควสต์", true),
          textField("quest_giver", "ผู้มอบหมาย"),
          selectField("status", "สถานะ", ["ยังไม่เริ่ม", "กำลังดำเนินการ", "สำเร็จแล้ว", "ล้มเหลว"]),
          longTextField("objectives", "เงื่อนไขเป้าหมาย"),
          textField("rewards", "รางวัลตอบแทน"),
        ],
        assistantInstruction: "อัปเดตสถานะของเควสต์เมื่อผู้เล่นทำเงื่อนไขสำเร็จ",
        contentType: "json", designation: "quest", contextVisibility: "pinned", imageStyleInstruction: "ภาพม้วนกระดาษโบราณ ตราประทับขี้ผึ้ง", showCoverImages: false, enabled: true,
      },
    },
    {
      category: { name: "กลุ่มอำนาจและสมาคม (Factions)", description: "กิลด์ ภาคี ตระกูล และลัทธิ", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🛡️" },
      template: {
        name: "Faction Dossier", description: "โครงสร้างอำนาจ อิทธิพล และอุดมการณ์",
        fields: [
          textField("name", "ชื่อกลุ่มหรือสมาคม", true),
          textField("leader", "ผู้นำ"),
          selectField("influence", "ระดับอิทธิพล", ["ระดับท้องถิ่น", "ระดับแคว้น", "ระดับอาณาจักร", "ระดับทวีป", "ระดับโลก"]),
          textField("headquarters", "ที่ตั้งศูนย์บัญชาการ"),
          longTextField("goals", "อุดมการณ์และเป้าหมายสูงสุด"),
          longTextField("allies_and_enemies", "พันธมิตรและคู่อริ"),
        ],
        assistantInstruction: "สะท้อนปฏิกิริยาของสมาชิกกลุ่มอำนาจตามชื่อเสียงและการกระทำของผู้เล่น",
        contentType: "json", designation: "faction", contextVisibility: "partial", imageStyleInstruction: "ภาพตราสัญลักษณ์ประจำตระกูลหรือกิลด์", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "สถานที่และดันเจี้ยน (Locations)", description: "เมือง ป่า ทะเลทราย และห้องลับ", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🏰" },
      template: {
        name: "Location Dossier", description: "บรรยากาศ จุดสนใจ และอันตรายที่ซ่อนอยู่",
        fields: [
          textField("name", "ชื่อสถานที่", true),
          textField("region", "ภูมิภาคหรือทวีป"),
          longTextField("atmosphere", "บรรยากาศ กลิ่น เสียง และสภาพแวดล้อม"),
          longTextField("points_of_interest", "จุดน่าสนใจหรือสิ่งปลูกสร้างสำคัญ"),
          longTextField("secrets", "ความลับ สิ่งซ่อนเร้น หรืออันตราย"),
        ],
        assistantInstruction: "ใช้บรรยายสิ่งแวดล้อมเมื่อผู้เล่นเดินทางมาถึงสถานที่นี้",
        contentType: "json", designation: "location", contextVisibility: "partial", imageStyleInstruction: "ภาพทิวทัศน์แนวนอน 16:9 สไตล์ fantasy environment concept art", showCoverImages: true, enabled: true,
      },
    },
  ],
};

// 2. Cyberpunk & Sci-Fi Pack
const CYBERPUNK_SCIFI_PACK: StarterPack = {
  id: "cyberpunk_scifi",
  title: "Cyberpunk & Sci-Fi Starter Pack",
  description: "ชุดเทมเพลตสำหรับโลกไซเบอร์พังก์และไซไฟ: แฮกเกอร์, AI, อวัยวะจักรกล, บรรษัทข้ามชาติ, ระบบดาว และแหล่งกบดาน",
  icon: "🚀",
  genre: "scifi",
  itemCount: 6,
  items: [
    {
      category: { name: "ตัวละครและสตรีท (Netrunners)", description: "แฮกเกอร์ ทหารรับจ้าง และคนสู้ชีวิตข้างถนน", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🕶️" },
      template: {
        name: "Cyber Character", description: "ข้อมูลแฮนด์เดิล ชื่อเสียง และหนี้สิน",
        fields: [
          textField("handle", "แฮนด์เดิล/ชื่อในวงการ", true),
          textField("real_name", "ชื่อจริง"),
          selectField("archetype", "บทบาท (Archetype)", ["Netrunner", "Solo", "Techie", "Fixer", "Corpo Deserter", "Nomad"]),
          numberField("street_cred", "Street Cred (ระดับความน่าเชื่อถือ)"),
          textField("cyberware_summary", "ไซเบอร์แวร์หลัก"),
          textField("debt", "หนี้สินหรือสัญญาผูกมัด"),
          longTextField("motivation", "เป้าหมายและจุดยืน"),
        ],
        assistantInstruction: "ดำเนินเรื่องด้วยน้ำเสียงคมคาย ดิบเถื่อน และทันสมัยตามสไตล์ไซเบอร์พังก์",
        contentType: "json", designation: "character", contextVisibility: "partial", imageStyleInstruction: "ภาพแนวไซเบอร์พังก์ แสงนีออน สายเคเบิลเชื่อมต่อ", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "ตัวตนดิจิทัลและ AI (Personas)", description: "ปัญญาประดิษฐ์ อิสระ และวิญญาณในระบบ", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🌐" },
      template: {
        name: "Online Persona / AI", description: "สเปก AI เซิร์ฟเวอร์ และไฟร์วอลล์",
        fields: [
          textField("alias", "นามเรียกขาน (Alias)", true),
          textField("subnet", "เครือข่าย/ซับเน็ตที่สิงสถิต"),
          selectField("threat_level", "ระดับอันตราย", ["ต่ำ (เครื่องมือทั่วไป)", "ปานกลาง (AI อัตโนมัติ)", "สูง (Black ICE/Rogue AI)", "ภัยพิบัติระดับเครือข่าย"]),
          numberField("firewall_rating", "ความหนาของไฟร์วอลล์ (Rating)"),
          longTextField("core_directive", "คำสั่งหลักหรือเจตจำนง"),
        ],
        assistantInstruction: "AI สนทนาด้วยลีลาโปรแกรมที่เยือกเย็นหรือแฝงความไม่เสถียร",
        contentType: "json", designation: "npc", contextVisibility: "searchable", imageStyleInstruction: "ภาพโฮโลแกรมดิจิทัล เส้นสายข้อมูลเรืองแสง", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "ไซเบอร์แวร์และเทคโนโลยี (Cyberware)", description: "อวัยวะเทียม อาวุธฝังในร่างกาย และชิปประสาท", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🦾" },
      template: {
        name: "Cyberware Spec", description: "คุณสมบัติ ความเข้ากันได้ และผลกระทบต่อจิต",
        fields: [
          textField("name", "ชื่ออุปกรณ์", true),
          textField("manufacturer", "ผู้ผลิต (Manufacturer)"),
          selectField("grade", "เกรด", ["ของเถื่อน (Black Market)", "มาตรฐานพลเรือน", "ระดับทหาร (Military Grade)", "ต้นแบบทดลอง (Prototype)"]),
          textField("body_location", "ตำแหน่งติดตั้ง (เช่น ดวงตา, แขนกล, กระดูกสันหลัง)"),
          longTextField("effects", "คุณสมบัติและความสามารถ"),
          textField("side_effects", "ผลข้างเคียงหรือการกินพลังงาน"),
        ],
        assistantInstruction: "คำนึงถึงความเสี่ยงและความล้ำหน้าทางเทคโนโลยีเมื่อเปิดใช้งาน",
        contentType: "json", designation: "item", contextVisibility: "searchable", imageStyleInstruction: "ภาพพิมพ์เขียวอวัยวะจักรกล ไซไฟ ละเอียดสูง", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "บรรษัทข้ามชาติ (Megacorps)", description: "บริษัทมหาอำนาจผู้ควบคุมทรัพยากรและรัฐบาล", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🏢" },
      template: {
        name: "Megacorp Dossier", description: "ข้อมูลทุน อุตสาหกรรม และกองกำลังลับ",
        fields: [
          textField("name", "ชื่อบรรษัท", true),
          textField("ceo", "ประธานเจ้าหน้าที่บริหาร (CEO)"),
          textField("primary_industry", "ธุรกิจหลัก (เช่น อาวุธ, ไบโอเทค, พลังงาน)"),
          selectField("security_tier", "ระดับกองกำลังความปลอดภัย", ["ยามตรวจการณ์ทั่วไป", "บอดี้การ์ดติดอาวุธ", "หน่วยรบพิเศษ", "กองทัพเอกชนเต็มรูปแบบ"]),
          longTextField("agenda", "วาระลับและโครงการวิจัยต้องห้าม"),
        ],
        assistantInstruction: "บรรษัทจะทำทุกวิถีทางเพื่อปกป้องความลับและผลกำไรของตนเอง",
        contentType: "json", designation: "faction", contextVisibility: "partial", imageStyleInstruction: "ภาพโลโก้องค์กรสไตล์มินิมอลบนตึกระฟ้าสะท้อนแสงนีออน", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "ระบบดาวและเขตปกครอง (Star Systems)", description: "สถานีอวกาศ ดาวเคราะห์ และเขตแดนในกาแล็กซี", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🪐" },
      template: {
        name: "Star System Chart", description: "พิกัด สภาพอากาศ และระดับความปลอดภัย",
        fields: [
          textField("name", "ชื่อระบบดาว/สถานี", true),
          textField("coordinates", "พิกัดดาราศาสตร์"),
          selectField("security_level", "ระดับความสงบ", ["ปลอดภัยสูง (High-Sec)", "เขตควบคุม (Mid-Sec)", "เถื่อนไร้กฎหมาย (Null-Sec)"]),
          numberField("habitable_planets", "จำนวนดาวที่อยู่อาศัยได้"),
          longTextField("dominant_factions", "กลุ่มที่มีอิทธิพลในพื้นที่"),
        ],
        assistantInstruction: "อิงระยะเวลาและกฎระเบียบตามระดับความปลอดภัยของระบบดาว",
        contentType: "json", designation: "location", contextVisibility: "partial", imageStyleInstruction: "ภาพอวกาศ ดาวเคราะห์ และสถานีวงแหวนไซไฟ", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "จุดนัดพบและบาร์ใต้ดิน (Establishments)", description: "บาร์สตรีท คลินิกเถื่อน และแหล่งซื้อขายข้อมูล", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🍸" },
      template: {
        name: "Establishment", description: "เจ้าของ บรรยากาศ และสินค้ามืด",
        fields: [
          textField("name", "ชื่อสถานที่", true),
          textField("proprietor", "เจ้าของหรือ Fixer ประจำร้าน"),
          longTextField("vibe", "แสง สี เสียง และบรรยากาศ"),
          longTextField("contraband_available", "ข้อมูลหรือสินค้าใต้ดินที่มีบริการ"),
        ],
        assistantInstruction: "เป็นจุดศูนย์กลางของการแลกเปลี่ยนข้อมูลและรับงานจ้าง",
        contentType: "json", designation: "location", contextVisibility: "partial", imageStyleInstruction: "ภาพภายในบาร์มืดสลัว แสงไฟนีออนสีม่วงแดง", showCoverImages: true, enabled: true,
      },
    },
  ],
};

// 3. Mystery & Detective Pack
const MYSTERY_DETECTIVE_PACK: StarterPack = {
  id: "mystery_detective",
  title: "Mystery & Detective Starter Pack",
  description: "ชุดเทมเพลตสำหรับนิยายสืบสวนและปมฆาตกรรม: นักสืบ, ผู้ต้องสงสัย, เบาะแส, แผนผังความสัมพันธ์, และสถานที่เกิดเหตุ",
  icon: "🔍",
  genre: "mystery",
  itemCount: 6,
  items: [
    {
      category: { name: "นักสืบและผู้ไขคดี (Investigators)", description: "นักสืบเอกชน ตำรวจสายสืบ หรือผู้ค้นหาความจริง", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🕵️" },
      template: {
        name: "Investigator Profile", description: "ความสามารถในการอนุมาน บาดแผล และแรงผลักดัน",
        fields: [
          textField("name", "ชื่อนักสืบ", true),
          textField("agency", "สังกัดหรือสำนักงาน"),
          textField("deduction_style", "แนวทางการสืบสวน (เช่น หลักฐานทางนิติวิทยาศาสตร์, จิตวิทยา)"),
          longTextField("trauma", "ปมในอดีตหรือบาดแผลทางใจ"),
          longTextField("personal_drive", "แรงผลักดันที่ต้องปิดคดีนี้"),
        ],
        assistantInstruction: "กระตุ้นให้นักสืบพบเห็นรายละเอียดเล็กๆ น้อยๆ ที่คนทั่วไปมองข้าม",
        contentType: "json", designation: "character", contextVisibility: "partial", imageStyleInstruction: "ภาพนักสืบในเสื้อโค้ตกลางสายฝน สไตล์ฟิล์มนัวร์", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "ผู้ต้องสงสัยและพยาน (Suspects)", description: "ผู้เกี่ยวข้องกับคดี แรงจูงใจ และข้ออ้างถิ่นที่อยู่", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "👥" },
      template: {
        name: "Suspect Dossier", description: "ความสัมพันธ์กับเหยื่อ ข้ออ้าง และพิรุธ",
        fields: [
          textField("name", "ชื่อผู้ต้องสงสัย", true),
          textField("relationship_to_victim", "ความสัมพันธ์กับเหยื่อ"),
          longTextField("alibi", "ข้ออ้างถิ่นที่อยู่เวลาเกิดเหตุ (Alibi)"),
          longTextField("motive", "แรงจูงใจที่เป็นไปได้"),
          textField("tell", "พิรุธหรือพฤติกรรมเวลาโกหก"),
          selectField("suspicion_level", "ระดับความน่าสงสัย", ["พยานบริสุทธิ์", "น่าสงสัยเล็กน้อย", "มีพิรุธชัดเจน", "ผู้ต้องสงสัยอันดับหนึ่ง"]),
        ],
        assistantInstruction: "พยานอาจพูดความจริงครึ่งเดียว ไม่ยอมเปิดเผยความลับจนกว่าจะมีหลักฐานมัดตัว",
        contentType: "json", designation: "npc", contextVisibility: "partial", imageStyleInstruction: "ภาพถ่ายพอร์ตเทรตบุคคล สีหน้ามีความกังวลหรือซ่อนเร้น", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "เบาะแสและวัตถุพยาน (Evidence)", description: "หลักฐานในที่เกิดเหตุ รอยนิ้วมือ และจดหมายลับ", enabled: true, visibleToPlayers: true, displayStyle: "list", icon: "🔎" },
      template: {
        name: "Evidence Card", description: "สภาพที่พบ และผลการวิเคราะห์",
        fields: [
          textField("name", "ชื่อหลักฐาน", true),
          textField("found_at", "สถานที่พบ"),
          textField("condition", "สภาพของวัตถุพยาน"),
          longTextField("forensic_details", "การตรวจสอบอย่างละเอียดหรือร่องรอย"),
          longTextField("connection", "ความเชื่อมโยงกับผู้ต้องสงสัยหรือคดี"),
        ],
        assistantInstruction: "หลักฐานแต่ละชิ้นต้องต่อกันเป็นจิ๊กซอว์ที่สมเหตุสมผล",
        contentType: "json", designation: "item", contextVisibility: "searchable", imageStyleInstruction: "ภาพหลักฐานในถุงพลาสติกเก็บพยานหลักฐาน มีป้ายกำกับหมายเลข", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "แผนผังความสัมพันธ์ (Relationships)", description: "ความรัก ความแค้น ผลประโยชน์ และความลับร่วม", enabled: true, visibleToPlayers: true, displayStyle: "list", icon: "🕸️" },
      template: {
        name: "Relationship Matrix", description: "สายสัมพันธ์ระหว่างสองบุคคล",
        fields: [
          textField("person_a", "บุคคลที่หนึ่ง", true),
          textField("person_b", "บุคคลที่สอง", true),
          selectField("connection_type", "ประเภทความสัมพันธ์", ["คนรัก", "คนในครอบครัว", "หุ้นส่วนธุรกิจ", "ลูกหนี้-เจ้าหนี้", "ศัตรูคู่อาฆาต", "ชู้รักลับ"]),
          longTextField("shared_secret", "ความลับที่รู้กันเพียงสองคน"),
        ],
        assistantInstruction: "ใช้ความสัมพันธ์นี้เป็นแรงขับเคลื่อนปฏิกิริยาและบทสนทนา",
        contentType: "json", designation: "rule", contextVisibility: "partial", imageStyleInstruction: "ภาพเส้นด้ายสีแดงโยงระหว่างภาพถ่ายสองใบ", showCoverImages: false, enabled: true,
      },
    },
    {
      category: { name: "สถานที่เกิดเหตุ (Crime Scenes)", description: "ห้องปิดตาย ตรอกมืด และคฤหาสน์ต้องสงสัย", enabled: true, visibleToPlayers: true, displayStyle: "grid", icon: "🏚️" },
      template: {
        name: "Crime Scene", description: "จุดสังเกต ทางเข้าออก และจุดที่พบศพ",
        fields: [
          textField("name", "ชื่อสถานที่เกิดเหตุ", true),
          textField("entry_points", "ทางเข้า-ออก และการล็อก"),
          longTextField("initial_findings", "สภาพแรกเห็นของสถานที่"),
          longTextField("hidden_details", "จุดที่ถูกเคลื่อนย้ายหรือทำลายหลักฐาน"),
        ],
        assistantInstruction: "ให้ความสำคัญกับรายละเอียดเชิงพื้นที่และเวลาเข้าออก",
        contentType: "json", designation: "location", contextVisibility: "partial", imageStyleInstruction: "ภาพห้องสลัว มีเทปกั้นสีเหลือง POLICE LINE DO NOT CROSS", showCoverImages: true, enabled: true,
      },
    },
    {
      category: { name: "นาฬิกากดดันและเวลา (Pressure Clocks)", description: "เวลานับถอยหลังก่อนเกิดเหตุร้ายครั้งต่อไป", enabled: true, visibleToPlayers: true, displayStyle: "list", icon: "⏱️" },
      template: {
        name: "Countdown Clock", description: "เข็มนาฬิกาและผลกระทบเมื่อหมดเวลา",
        fields: [
          textField("name", "ชื่อเหตุการณ์กดดัน", true),
          numberField("current_ticks", "จำนวนขีดปัจจุบัน"),
          numberField("max_ticks", "ขีดสูงสุด (เช่น 4, 6 หรือ 8 ขีด)"),
          longTextField("consequence", "สิ่งที่จะเกิดขึ้นเมื่อนาฬิกาเดินเต็มขีด"),
        ],
        assistantInstruction: "เพิ่มขีดนาฬิกาเมื่อผู้เล่นเสียเวลาหรือตัดสินใจผิดพลาด",
        contentType: "json", designation: "condition", contextVisibility: "pinned", imageStyleInstruction: "ภาพนาฬิกาโบราณหรือเกจวัดแรงดัน", showCoverImages: false, enabled: true,
      },
    },
  ],
};

export const ALL_STARTER_PACKS: StarterPack[] = [
  FANTASY_DND_PACK,
  CYBERPUNK_SCIFI_PACK,
  MYSTERY_DETECTIVE_PACK,
];

export function getStarterPacks(): StarterPack[] {
  return ALL_STARTER_PACKS;
}

export function applyStarterPack(
  packId: string,
  currentCategories: CardCategoryDocument[],
  currentTemplates: CardTemplateDocument[]
): { categories: CardCategoryDocument[]; templates: CardTemplateDocument[]; addedCount: number } {
  const pack = ALL_STARTER_PACKS.find((p) => p.id === packId);
  if (!pack) return { categories: currentCategories, templates: currentTemplates, addedCount: 0 };

  const newCategories = [...currentCategories];
  const newTemplates = [...currentTemplates];
  let addedCount = 0;

  const now = Date.now();
  for (const item of pack.items) {
    const categoryId = crypto.randomUUID();
    const templateId = crypto.randomUUID();

    const categoryDoc: CardCategoryDocument = {
      id: categoryId,
      name: item.category.name,
      description: item.category.description,
      templateId,
      order: newCategories.length,
      enabled: item.category.enabled,
      visibleToPlayers: item.category.visibleToPlayers,
      displayStyle: item.category.displayStyle,
      icon: item.category.icon,
      updatedAt: now,
    };

    const templateDoc = withRecommendedLayouts({
      id: templateId,
      categoryId,
      name: item.template.name,
      description: item.template.description,
      version: 1,
      fields: item.template.fields,
      assistantInstruction: item.template.assistantInstruction,
      contentType: item.template.contentType,
      designation: item.template.designation,
      contextVisibility: item.template.contextVisibility,
      imageStyleInstruction: item.template.imageStyleInstruction,
      showCoverImages: item.template.showCoverImages,
      enabled: item.template.enabled,
      updatedAt: now,
    });

    newCategories.push(categoryDoc);
    newTemplates.push(templateDoc);
    addedCount++;
  }

  return { categories: newCategories, templates: newTemplates, addedCount };
}

export interface CraftStarterProjectData {
  categories: CardCategoryDocument[];
  templates: CardTemplateDocument[];
  cards: StructuredCardDocument[];
  creatorInstructions: CreatorInstructionDocument[];
}

export function createCraftStarterProjectFiles(): CraftStarterProjectData {
  const now = Date.now();

  // 1. Characters Category & Template
  const charCatId = "cat_characters_default";
  const charTmplId = "tmpl_characters_default";
  const charCat: CardCategoryDocument = {
    id: charCatId,
    name: "Characters",
    description: "Player characters, key NPCs, allies, and rivals inhabiting the world.",
    templateId: charTmplId,
    order: 0,
    enabled: true,
    visibleToPlayers: true,
    displayStyle: "grid",
    icon: "🧙‍♂️",
    updatedAt: now,
  };
  const charTmpl: CardTemplateDocument = {
    id: charTmplId,
    categoryId: charCatId,
    name: "Character Sheet",
    description: "Detailed entity persona, role, background, and capability attributes.",
    version: 1,
    fields: [
      textField("name", "Name", true),
      textField("characterClass", "Class / Role", false),
      textField("ancestry", "Ancestry / Species", false),
      longTextField("personality", "Personality & Demeanor", false),
      longTextField("background", "Background & Lore", false),
    ],
    assistantInstruction: "Portray this character with consistency in personality, motive, and background.",
    contentType: "json",
    designation: "character",
    contextVisibility: "partial",
    imageStyleInstruction: "Fantasy character concept portrait, digital painting, dramatic lighting",
    showCoverImages: true,
    enabled: true,
    updatedAt: now,
  };
  const charCard: StructuredCardDocument = {
    id: "card_example_character",
    categoryId: charCatId,
    templateId: charTmplId,
    templateVersion: 1,
    title: "Example Character",
    summary: "A seasoned ranger of the borderlands with sharp senses and deep knowledge of ancient paths.",
    status: "ready",
    source: "preset",
    values: {
      name: "Eldrin Whisperwind",
      characterClass: "Ranger / Frontier Scout",
      ancestry: "Half-Elf",
      gender: "Male",
      age: "42",
      currentLocation: "The Crossroads Tavern",
      personality: "Calm, observant, speaks thoughtfully and values trustworthiness.",
      background: "Spent decades mapping uncharted wilderness and guarding border settlements from wild beasts.",
      stats: { strength: 13, dexterity: 16, constitution: 14, intelligence: 12, wisdom: 15, charisma: 10 },
      hitPoints: "28 / 28",
    },
    images: [],
    triggers: ["Eldrin", "Whisperwind", "ranger", "scout"],
    contextVisibility: "partial",
    triggerMode: "default",
    enabled: true,
    visibleToPlayers: true,
    updatedAt: now,
  };

  // 2. Locations Category & Template
  const locCatId = "cat_locations_default";
  const locTmplId = "tmpl_locations_default";
  const locCat: CardCategoryDocument = {
    id: locCatId,
    name: "Locations",
    description: "Settlements, taverns, dungeons, landmarks, and mystical realms.",
    templateId: locTmplId,
    order: 1,
    enabled: true,
    visibleToPlayers: true,
    displayStyle: "grid",
    icon: "🏰",
    updatedAt: now,
  };
  const locTmpl: CardTemplateDocument = {
    id: locTmplId,
    categoryId: locCatId,
    name: "Location Sheet",
    description: "Atmosphere, sensory details, geography, and hidden secrets.",
    version: 1,
    fields: [
      textField("name", "Location Name", true),
      textField("environment", "Environment / Region", false),
      longTextField("description", "Atmosphere & Sensory Description", false),
      longTextField("secrets", "Hidden Features / GM Secrets", false),
    ],
    assistantInstruction: "Immerse players in this location through rich sensory prose and environmental cues.",
    contentType: "json",
    designation: "location",
    contextVisibility: "searchable",
    imageStyleInstruction: "Atmospheric landscape environment concept art, high detail, golden hour",
    showCoverImages: true,
    enabled: true,
    updatedAt: now,
  };
  const locCard: StructuredCardDocument = {
    id: "card_example_location",
    categoryId: locCatId,
    templateId: locTmplId,
    templateVersion: 1,
    title: "Example Location",
    summary: "A warm, timber-built haven nestled at the junction of two ancient trade highways.",
    status: "ready",
    source: "preset",
    values: {
      name: "The Crossroads Tavern",
      environment: "Highland Forest Crossroads",
      description: "Thick timber logs, a roaring fieldstone fireplace, the rich aroma of roasted venison and spiced cider, and weary travelers conversing in quiet murmurs.",
      secrets: "A subterranean tunnel beneath the cellar connects directly to an overgrown watchtower ruins.",
      climate: "Temperate highland, crisp autumn breezes",
      notableNpcs: "Innkeeper Barnaby, Eldrin Whisperwind",
    },
    images: [],
    triggers: ["Crossroads Tavern", "tavern", "inn", "crossroads"],
    contextVisibility: "searchable",
    triggerMode: "default",
    enabled: true,
    visibleToPlayers: true,
    updatedAt: now,
  };

  // 3. Equipment Category & Template
  const equipCatId = "cat_equipment_default";
  const equipTmplId = "tmpl_equipment_default";
  const equipCat: CardCategoryDocument = {
    id: equipCatId,
    name: "Equipment",
    description: "Weapons, armor, alchemical potions, and survival gear.",
    templateId: equipTmplId,
    order: 2,
    enabled: true,
    visibleToPlayers: true,
    displayStyle: "list",
    icon: "⚔️",
    updatedAt: now,
  };
  const equipTmpl: CardTemplateDocument = {
    id: equipTmplId,
    categoryId: equipCatId,
    name: "Equipment Card",
    description: "Item specifications, rarity, weight, and mechanical effects.",
    version: 1,
    fields: [
      textField("name", "Item Name", true),
      textField("itemType", "Item Type / Category", false),
      textField("rarity", "Rarity Tier", false),
      longTextField("description", "Physical Appearance & Description", false),
      longTextField("effects", "Rules & Tactical Effects", false),
    ],
    assistantInstruction: "Reflect this equipment's properties and inventory presence in player actions.",
    contentType: "json",
    designation: "item",
    contextVisibility: "searchable",
    imageStyleInstruction: "Item illustration isolated on dark fantasy background, high detail RPG icon",
    showCoverImages: true,
    enabled: true,
    updatedAt: now,
  };
  const equipCards: StructuredCardDocument[] = [
    {
      id: "card_equip_healing_potion",
      categoryId: equipCatId,
      templateId: equipTmplId,
      templateVersion: 1,
      title: "Healing Potion",
      summary: "A vial of shimmering crimson draught that knits wounds on contact.",
      status: "ready",
      source: "preset",
      values: {
        name: "Healing Potion",
        itemType: "Potion / Consumable",
        rarity: "Common",
        description: "A small crystal flask with a wax seal, containing effervescent red liquid smelling of sweet berries and herbs.",
        effects: "Drinking or administering this potion restores 2d4 + 2 hit points immediately.",
        weight: "0.5 lb",
        value: "50 gp",
      },
      images: [],
      triggers: ["healing potion", "potion", "heal"],
      contextVisibility: "searchable",
      triggerMode: "default",
      enabled: true,
      visibleToPlayers: true,
      updatedAt: now,
    },
    {
      id: "card_equip_iron_shortsword",
      categoryId: equipCatId,
      templateId: equipTmplId,
      templateVersion: 1,
      title: "Iron Shortsword",
      summary: "A balanced, double-edged blade forged for agile and close-quarters thrusting.",
      status: "ready",
      source: "preset",
      values: {
        name: "Iron Shortsword",
        itemType: "Martial Weapon (Finesse, Light)",
        rarity: "Common",
        description: "Cleanly ground iron blade, cruciform brass crossguard, and wire-bound dark leather grip.",
        effects: "Deals 1d6 piercing damage. Uses Dexterity or Strength for attack and damage rolls.",
        weight: "2 lbs",
        value: "10 gp",
      },
      images: [],
      triggers: ["iron shortsword", "shortsword", "sword"],
      contextVisibility: "searchable",
      triggerMode: "default",
      enabled: true,
      visibleToPlayers: true,
      updatedAt: now,
    },
    {
      id: "card_equip_leather_armor",
      categoryId: equipCatId,
      templateId: equipTmplId,
      templateVersion: 1,
      title: "Leather Armor",
      summary: "Flexible boiled leather cuirass offering defense without sacrificing stealth.",
      status: "ready",
      source: "preset",
      values: {
        name: "Leather Armor",
        itemType: "Light Armor",
        rarity: "Common",
        description: "Hardened boiled leather breastplate and shoulder pauldrons tailored over a soft linen gambeson.",
        effects: "Provides Armor Class (AC) 11 + Dexterity modifier. Imposes no stealth disadvantage.",
        weight: "10 lbs",
        value: "10 gp",
      },
      images: [],
      triggers: ["leather armor", "armor", "cuirass"],
      contextVisibility: "searchable",
      triggerMode: "default",
      enabled: true,
      visibleToPlayers: true,
      updatedAt: now,
    },
    {
      id: "card_equip_adventurers_pack",
      categoryId: equipCatId,
      templateId: equipTmplId,
      templateVersion: 1,
      title: "Adventurer's Pack",
      summary: "A durable canvas expedition pack containing standard wilderness survival essentials.",
      status: "ready",
      source: "preset",
      values: {
        name: "Adventurer's Pack",
        itemType: "Survival Gear Kit",
        rarity: "Common",
        description: "Waterproofed canvas rucksack buckled with brass fittings and lashings for a bedroll.",
        effects: "Contains: bedroll, mess kit, tinderbox, 10 torches, 10 days of rations, waterskin, and 50 feet of hempen rope.",
        weight: "35 lbs",
        value: "12 gp",
      },
      images: [],
      triggers: ["adventurers pack", "backpack", "pack", "supplies"],
      contextVisibility: "searchable",
      triggerMode: "default",
      enabled: true,
      visibleToPlayers: true,
      updatedAt: now,
    },
  ];

  // 4. Game Starts Category & Template
  const startsCatId = "cat_game_starts_default";
  const startsTmplId = "tmpl_game_starts_default";
  const startsCat: CardCategoryDocument = {
    id: startsCatId,
    name: "Game Starts",
    description: "Introductory scenarios and branching prologue choices for starting play.",
    templateId: startsTmplId,
    order: 3,
    enabled: true,
    visibleToPlayers: true,
    displayStyle: "feature_grid",
    icon: "🚀",
    updatedAt: now,
  };
  const startsTmpl: CardTemplateDocument = {
    id: startsTmplId,
    categoryId: startsCatId,
    name: "Game Start Option",
    description: "Opening premise, mood, player hooks, and initial situation.",
    version: 1,
    fields: [
      textField("title", "Scenario Title", true),
      longTextField("premise", "Opening Premise & Hook", false),
      longTextField("initialScene", "Initial Situation Narrative", false),
    ],
    assistantInstruction: "Use this opening premise to establish immediate momentum and player agency.",
    contentType: "json",
    designation: "game_start",
    contextVisibility: "pinned",
    imageStyleInstruction: "Dramatic fantasy story opening illustration, evocative cinematic scene",
    showCoverImages: true,
    enabled: true,
    updatedAt: now,
  };
  const startCard: StructuredCardDocument = {
    id: "card_example_game_start",
    categoryId: startsCatId,
    templateId: startsTmplId,
    templateVersion: 1,
    title: "Tavern Start",
    summary: "Begin your adventure seeking shelter from a brewing autumn tempest at the Crossroads Tavern.",
    status: "ready",
    source: "preset",
    values: {
      title: "Tavern Start",
      premise: "Thunder rumbles across the frontier highlands. You push open the heavy oak doors of the Crossroads Tavern as sheets of cold rain begin to pour.",
      initialScene: "The hearth warmth immediately washes over you. Across the common room, a cloaked ranger catches your eye, gesturing slightly toward an empty wooden stool.",
      startingLocation: "The Crossroads Tavern",
      initialHook: "A sealed courier cylinder lies half-concealed beneath a napkin on the corner table.",
    },
    images: [],
    triggers: ["start", "opening", "prologue", "tavern start"],
    contextVisibility: "pinned",
    triggerMode: "default",
    enabled: true,
    visibleToPlayers: true,
    updatedAt: now,
  };

  // 5. Creator Instructions (GM Guidelines)
  const instruction: CreatorInstructionDocument = {
    id: "inst_example_gm_instructions",
    title: "Example GM Instructions",
    content: "1. Roleplay as an evocative, immersive Game Master using purely literary prose without HTML tags or JSON markers.\n2. When player actions reference characters or locations, maintain continuity with the project's structured files.\n3. Track inventory changes, stamina, and world consequences dynamically in every scene.\n4. Encourage player creativity with meaningful choices, sensory environmental details, and escalating stakes.",
    summary: "Foundational GM narrative rules and world continuity instructions",
    triggers: ["gm", "rules", "instructions", "game master"],
    priority: 90,
    pinned: true,
    enabled: true,
    updatedAt: now,
  };

  return {
    categories: [charCat, locCat, equipCat, startsCat],
    templates: [charTmpl, locTmpl, equipTmpl, startsTmpl].map(withRecommendedLayouts),
    cards: [charCard, locCard, ...equipCards, startCard],
    creatorInstructions: [instruction],
  };
}
